import type { NodeV2 } from '@vedalib/editor'
import { isComplexElement, isNodeV2 } from '@vedalib/editor'
import * as R from 'ramda'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import {
  setNavbar,
  setProjectNavigation,
  setShowGrid,
  updateBlock,
} from 'services/Store/Project/actions'
import { DeviceMode, NavigationEnum } from 'services/Store/Project/enums'
import {
  addGridRows,
  moveCoordinates,
  moveGridArea,
  moveNode,
} from 'services/Store/Project/mutators/node'
import {
  getBlock,
  getDeviceMode,
  getSelectedNodeIds,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import store from 'services/Store/store'
import { t } from 'services/Translation'
import { isCtrl } from 'utils/events'

import { safeParseArea } from '../formControls/Area/utils'
import { safeParseGrid } from '../formControls/Grid/utils'

const SHOW_GRID_TIMEOUT = 500

const ARROW_MAP: Record<string, 'up' | 'down' | 'left' | 'right'> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
}

export const onAddRowUp = (event?: KeyboardEvent) => {
  const state = store.getState().project
  const dispatch = store.dispatch
  const block = getBlock(state)
  const selectedNodes = getSelectedNodeIds(state)
  const device = getDeviceMode(state)
  const parent = getParentNode(selectedNodes?.[0], block)
  if (block && parent && selectedNodes.length) {
    event?.preventDefault()
    const parentStyle = mergeNodeStyle(parent.style, device)
    const rows = safeParseGrid(parentStyle.gridRows)

    if (parent && parentStyle.display === 'grid') {
      const styles = selectedNodes.map((id) => {
        const node = block.schema.nodes[id] as NodeV2
        return mergeNodeStyle(
          node.style,
          device,
          node.elementId ? block.elements[node.elementId]?.type : undefined,
        )
      })
      const areas = styles.map((style) => safeParseArea(style.gridArea))
      const rowStart = Math.min(...areas.map((area) => area.rowStart)) || 0
      const index = rowStart - 1
      const row = rows.items[R.clamp(1, rows.items.length - 1, index)]?.data

      const bottomChildren = parent?.children
        .filter((id) => {
          const child = block.schema.nodes[id]
          if (!isNodeV2(child)) {
            return false
          }
          const childArea = safeParseArea(mergeNodeStyle(child.style, device).gridArea)
          return childArea.rowStart >= rowStart
        })
        .map((id) => block.schema.nodes[id])
        .filter(isNodeV2)

      const newBlock = R.compose(
        ...(bottomChildren?.map((child) => moveGridArea(child.id, device, ['down'])) as [
          (b: Block) => Block,
        ]),
        addGridRows(parent.id, device, 1, index, row),
      )(block)
      dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
    }
  }
}

export const onAddRowDown = (event?: KeyboardEvent) => {
  const state = store.getState().project
  const dispatch = store.dispatch
  const block = getBlock(state)
  const selectedNodes = getSelectedNodeIds(state)
  const device = getDeviceMode(state)
  const parent = getParentNode(selectedNodes?.[0], block)
  if (block && parent && selectedNodes.length) {
    event?.preventDefault()
    const parentStyle = mergeNodeStyle(parent.style, device)
    const rows = safeParseGrid(parentStyle.gridRows)
    const styles = selectedNodes.map((id) => {
      const node = block.schema.nodes[id] as NodeV2
      return mergeNodeStyle(
        node.style,
        device,
        node.elementId ? block.elements[node.elementId]?.type : undefined,
      )
    })
    const areas = styles.map((style) => safeParseArea(style.gridArea))
    const rowEnd = Math.max(...areas.map((area) => area.rowEnd)) || 0
    const index = rowEnd - 1
    const row = rows.items[R.clamp(1, rows.items.length - 1, index - 1)]?.data

    const bottomChildren = parent?.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, device).gridArea)
        return childArea.rowStart >= rowEnd
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(bottomChildren?.map((child) => moveGridArea(child.id, device, ['down'])) as [
        (b: Block) => Block,
      ]),
      addGridRows(parent.id, device, 1, index, row),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }
}

export const onArrows = (() => {
  let showGridTimeout: ReturnType<typeof setTimeout>

  return (event: KeyboardEvent) => {
    const dispatch = store.dispatch
    const state = store.getState().project
    const block = getBlock(state)
    const selectedNodes = getSelectedNodeIds(state)
    const device = getDeviceMode(state)
    const isDesktop = device === DeviceMode.desktop
    const parent = getParentNode(selectedNodes[0], block)
    const dir = ARROW_MAP[event.key]

    const isEveryAbsolute = selectedNodes.every((id) => {
      const node = block?.schema.nodes[id] as NodeV2
      const style = mergeNodeStyle(node?.style || {}, device)
      return style.flexFlow === 'absolute'
    })

    if (block && event.shiftKey) {
      event.preventDefault()
      const node = selectedNodes.length > 0 ? block.schema.nodes[selectedNodes[0]] : null
      const element = isNodeV2(node) && node.elementId ? block?.elements[node.elementId] : null
      const index = parent?.children.findIndex((id) => id === node?.id) ?? -1

      const grandParent = parent ? getParentNode(parent.id, block) : null
      const grandParentElement =
        isNodeV2(grandParent) && grandParent.elementId
          ? block?.elements[grandParent.elementId]
          : null
      const parentIndex = isComplexElement(grandParentElement)
        ? (grandParentElement.value.items.findIndex(({ value }) => value === parent?.id) ?? -1)
        : (grandParent?.children.findIndex((id) => id === parent?.id) ?? -1)

      const prevNodeId = ~index ? parent?.children[index - 1] : null
      const prevParentId = parent?.id
      const prevId = prevNodeId || prevParentId
      const nextNodeId = ~index ? parent?.children[index + 1] : null
      const nextParentId = ~parentIndex ? grandParent?.children[parentIndex + 1] : null
      const nextId = nextNodeId || nextParentId
      const childId =
        node?.children[0] || (isComplexElement(element) ? element?.value.items[0]?.value : null)

      dispatch(setNavbar({ tab: NavigationEnum.block }))
      if (parent && dir === 'left') {
        dispatch(setProjectNavigation({ nodeIds: [parent.id] }))
      }
      if (childId && dir === 'right') {
        dispatch(setProjectNavigation({ nodeIds: [childId] }))
      }
      if (prevId && dir === 'up') {
        dispatch(setProjectNavigation({ nodeIds: [prevId] }))
      }
      if (nextId && dir === 'down') {
        dispatch(setProjectNavigation({ nodeIds: [nextId] }))
      }
    } else if (block && parent && selectedNodes.length) {
      event.preventDefault()

      const parentStyle = mergeNodeStyle(parent?.style || {}, device)

      if (parentStyle.display === 'grid' && isCtrl(event)) {
        if (dir === 'up') {
          onAddRowUp(event)
        }

        if (dir === 'down') {
          onAddRowDown(event)
        }
      }

      if (parentStyle.display === 'grid' && !isCtrl(event)) {
        const { gridColumns, gridRows } = parentStyle
        const cols = safeParseGrid(gridColumns)
        const rows = safeParseGrid(gridRows)
        const newBlock = R.compose(
          ...(selectedNodes.map((id) => {
            const node = block.schema.nodes[id] as NodeV2
            const nodeStyle = mergeNodeStyle(node?.style || {}, device)
            const gridArea = safeParseArea(nodeStyle.gridArea)
            if (dir === 'down' && gridArea.rowEnd >= rows.items.length + 1) {
              return R.identity
            }
            if (dir === 'up' && gridArea.rowStart <= 1) {
              return R.identity
            }
            if (dir === 'right' && gridArea.colEnd >= cols.items.length + 1) {
              return R.identity
            }
            if (dir === 'left' && gridArea.colStart <= 1) {
              return R.identity
            }
            return moveGridArea(id, device, [dir])
          }) as [(block: Block) => Block]),
        )(block)

        dispatch(updateBlock({ id: block.uuid, value: newBlock }))
      }

      if (
        parentStyle.display === 'absolute' ||
        (parentStyle.display === 'flex' && isEveryAbsolute)
      ) {
        const newBlock = R.compose(
          ...(selectedNodes.map((id) => {
            return moveCoordinates(id, device, [dir], isCtrl(event) ? 10 : 1)
          }) as [(block: Block) => Block]),
        )(block)

        dispatch(updateBlock({ id: block.uuid, value: newBlock }))
      }

      if (parentStyle.display === 'flex') {
        if (!isDesktop) {
          return notify({
            type: NotificationType.info,
            message: t('editorV2.notifyStructureChangesRestricted'),
          })
        }
        const newBlock = R.compose(
          ...(selectedNodes.map((id) => {
            const index = parent.children.findIndex((childId) => id === childId)
            const newIndex = dir === 'up' || dir === 'left' ? index - 1 : index + 2

            if (newIndex < 0 || newIndex > parent.children.length) {
              return R.identity
            }
            return R.compose(moveNode(parent.id, parent.id, id, newIndex))
          }) as [(block: Block) => Block]),
        )(block)
        dispatch(updateBlock({ id: block.uuid, value: newBlock }))
      }

      dispatch(setShowGrid({ nodeId: parent?.id, showGrid: true }))
      clearTimeout(showGridTimeout)
      showGridTimeout = setTimeout(() => {
        dispatch(setShowGrid({ nodeId: parent?.id, showGrid: false }))
      }, SHOW_GRID_TIMEOUT)
    }
  }
})()
